import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, Alert,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { forecastAPI, summaryAPI } from '../services/api';
import { ForecastChart, StatCard } from '../components/ChartWidget';
import ForecastSliders from '../components/ForecastSliders';
import Navbar from '../components/Navbar';

const defaultParams = {
  name: 'My Forecast',
  primary_monthly_income: 0,
  current_net_worth: 50000,
  base_monthly_expenses: 0,
  projection_years: 10,
  inflation_rate: 0.03,
  investment_return_rate: 0.07,
  children: [],
  goals: [],
  future_payments: [],
  additional_income: [],
};

export default function Forecast() {
  const [params, setParams] = useState(defaultParams);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [realDataLoaded, setRealDataLoaded] = useState(false);

  useEffect(() => {
    forecastAPI.scenarios().then((r) => setScenarios(r.data)).catch(() => {});
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const fromDate = sixMonthsAgo.toISOString().split('T')[0];
    summaryAPI.overview({ from_date: fromDate })
      .then((r) => {
        const d = r.data;
        if (d && (d.total_income > 0 || d.total_expenses > 0)) {
          setParams((prev) => ({
            ...prev,
            primary_monthly_income: Math.round(d.total_income / 6),
            base_monthly_expenses: Math.round(d.total_expenses / 6),
          }));
          setRealDataLoaded(true);
        }
      })
      .catch(() => {});
  }, []);

  const calculate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await forecastAPI.calculate(params);
      setResult(res.data);
    } catch (err) {
      setError('Calculation failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const saveScenario = async () => {
    try {
      await forecastAPI.createScenario({ name: scenarioName || params.name, parameters: params });
      setSaveOpen(false);
      const r = await forecastAPI.scenarios();
      setScenarios(r.data);
    } catch (err) {
      setError('Failed to save scenario');
    }
  };

  const loadScenario = (scenario) => {
    try {
      const p = JSON.parse(scenario.parameters);
      setParams(p);
      setResult(null);
    } catch {}
  };

  const deleteScenario = async (id) => {
    try {
      await forecastAPI.deleteScenario(id);
      setScenarios(scenarios.filter((s) => s.id !== id));
    } catch {}
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

  return (
    <>
      <Navbar />
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Typography variant="h4" fontWeight={700}>Financial Forecast</Typography>
          {realDataLoaded && (
            <Chip label="Using your 6-month averages" color="success" size="small" />
          )}
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <ForecastSliders params={params} onChange={setParams} />
                <Button variant="contained" fullWidth onClick={calculate} disabled={loading} sx={{ mt: 2 }}>
                  {loading ? <CircularProgress size={24} /> : 'Calculate Forecast'}
                </Button>
                <Button variant="outlined" fullWidth startIcon={<SaveIcon />}
                  onClick={() => { setScenarioName(params.name || ''); setSaveOpen(true); }}
                  sx={{ mt: 1 }}>
                  Save Scenario
                </Button>
              </CardContent>
            </Card>
            {scenarios.length > 0 && (
              <Card elevation={2} sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Saved Scenarios</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {scenarios.map((s) => (
                          <TableRow key={s.id} hover sx={{ cursor: 'pointer' }}>
                            <TableCell onClick={() => loadScenario(s)}>{s.name}</TableCell>
                            <TableCell align="right">
                              <Button size="small" color="error" onClick={() => deleteScenario(s.id)}>Delete</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            {result ? (
              <>
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={6} md={3}>
                    <StatCard title="Final Net Worth" value={formatCurrency(result.summary.final_net_worth)} color="primary.main" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatCard title="Final Monthly Savings" value={formatCurrency(result.summary.final_monthly_savings)} color="success.main" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatCard title="Projection" value={result.summary.total_years + ' years'} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatCard title="Milestones" value={result.milestones.length} />
                  </Grid>
                </Grid>
                <Card elevation={2} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>Net Worth Projection</Typography>
                    <ForecastChart data={result.data_points} />
                  </CardContent>
                </Card>
                {result.milestones.length > 0 && (
                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom>Milestones</Typography>
                      {result.milestones.map((m, i) => (
                        <Alert key={i} severity="success" sx={{ mb: 1 }}>{m.message}</Alert>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card elevation={2}>
                <CardContent>
                  <Typography color="text.secondary" textAlign="center" py={8}>
                    Adjust parameters and click Calculate Forecast to see your projection.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
        <Dialog open={saveOpen} onClose={() => setSaveOpen(false)}>
          <DialogTitle>Save Scenario</DialogTitle>
          <DialogContent>
            <TextField autoFocus fullWidth label="Scenario Name" value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveScenario}>Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
